/**
 * Connect's own WhatsApp: which client's WhatsApp service, and which of its
 * Authentication templates, carry clients' sign-in codes.
 *
 * Codes go by WhatsApp first and by SMS when WhatsApp cannot take one — nothing
 * chosen here, the choice no longer usable, or Meta refusing the number — so
 * clearing this never stops anybody signing in; it only stops the WhatsApp half.
 * The credentials are the chosen service's own, kept on that client's Services
 * tab: nothing secret is set here.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageCircle } from 'lucide-react';
import {
  Alert,
  ApiError,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  QueryPending,
  ReadOnly,
  Select,
  api,
  toast,
  useCan,
} from '@connect/web-shared';

interface SignInWhatsApp {
  channel_public_id: string | null;
  template_public_id: string | null;
  client_name: string | null;
  /** Everything chosen is still usable: the service active, the template on, a number approved. */
  active: boolean;
  options: {
    public_id: string;
    label: string;
    status: string;
    templates: { public_id: string; name: string; is_active: boolean }[];
  }[];
}

const signInWhatsAppKey = ['messaging', 'sign-in-whatsapp'] as const;

export function SignInWhatsAppCard() {
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.channel.edit');
  const current = useQuery({
    queryKey: signInWhatsAppKey,
    queryFn: () => api.get<SignInWhatsApp>('/messaging/sign-in-whatsapp'),
  });
  const [channel, setChannel] = useState('');
  const [template, setTemplate] = useState('');

  useEffect(() => {
    if (!current.data) return;
    setChannel(current.data.channel_public_id ?? '');
    setTemplate(current.data.template_public_id ?? '');
  }, [current.data]);

  const save = useMutation({
    mutationFn: (choice: { channel: string; template: string }) =>
      api.put<SignInWhatsApp>('/messaging/sign-in-whatsapp', {
        channel_public_id: choice.channel || null,
        template_public_id: choice.template || null,
      }),
    onSuccess: (saved) => {
      queryClient.setQueryData(signInWhatsAppKey, saved);
      toast.success(
        saved.channel_public_id ? 'Sign-in codes go by WhatsApp first' : 'Sign-in codes go by SMS only',
      );
    },
  });

  const data = current.data;
  const option = data?.options.find((o) => o.public_id === channel);
  const changed =
    channel !== (data?.channel_public_id ?? '') || template !== (data?.template_public_id ?? '');

  return (
    <ReadOnly when={!canEdit}>
      <Card>
        <CardHeader
          title="Sign-in codes by WhatsApp"
          actions={
            data?.channel_public_id ? (
              data.active ? (
                <Badge tone="success">In use</Badge>
              ) : (
                <Badge tone="warning">Not usable — SMS only</Badge>
              )
            ) : (
              <Badge tone="neutral">Off — SMS only</Badge>
            )
          }
        />
        <CardBody className="space-y-4">
          <p className="text-sm text-muted">
            A client's sign-in code goes by WhatsApp first, through the service chosen here, and by SMS
            when WhatsApp cannot deliver it.
          </p>
          {data === undefined ? (
            <QueryPending query={current} label="Loading" />
          ) : data.options.length === 0 ? (
            <p className="flex items-center gap-2 text-sm text-muted">
              <MessageCircle className="size-4" />
              No client has a WhatsApp service yet. Add one on the client's Services tab first.
            </p>
          ) : (
            <>
              {save.error ? (
                <Alert tone="danger" title="Could not save">
                  {save.error instanceof ApiError ? save.error.message : 'Could not reach the server.'}
                </Alert>
              ) : null}
              <div className="grid gap-4 sm:grid-cols-2">
                <Select
                  label="WhatsApp service"
                  placeholder="None — SMS only"
                  options={data.options.map((o) => ({
                    value: o.public_id,
                    label: o.status === 'active' ? o.label : `${o.label} (paused)`,
                  }))}
                  value={channel}
                  onChange={(event) => {
                    setChannel(event.target.value);
                    setTemplate('');
                  }}
                />
                <Select
                  label="Authentication template"
                  // Waits on the service above, and says so rather than sitting greyed out.
                  placeholder={
                    !option
                      ? 'Choose a WhatsApp service first'
                      : !option.templates.length
                        ? 'None synced on this service'
                        : 'Choose one'
                  }
                  options={(option?.templates ?? []).map((t) => ({
                    value: t.public_id,
                    label: t.is_active ? t.name : `${t.name} (off)`,
                  }))}
                  value={template} disabled={!option}
                  hint="Created in WhatsApp Manager with a Copy code button, then synced."
                  onChange={(event) => setTemplate(event.target.value)}
                />
              </div>
              {canEdit ? (
                <div className="flex justify-end gap-2">
                  {data.channel_public_id ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={save.isPending}
                      onClick={() => save.mutate({ channel: '', template: '' })}
                    >
                      Turn off
                    </Button>
                  ) : null}
                  <Button
                    size="sm"
                    loading={save.isPending}
                    disabled={!changed || !channel || !template}
                    onClick={() => save.mutate({ channel, template })}
                  >
                    Save
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </CardBody>
      </Card>
    </ReadOnly>
  );
}
